import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/shared/AuthContext";
import { AuthServiceError } from "../types/auth";
import { Alert, Button, Spinner, TextInput } from "../components/ui";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login({ username, password });
      navigate("/dashboard");
    } catch (err: any) {
      if (err?.status === 403) {
        navigate("/not-authorized", { replace: true });
        return;
      }

      if (err?.status === 401) {
        setError("Invalid username or password.");
      } else {
        setError(
          err?.message || "Failed to log in. Please check your credentials.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-canvas flex min-h-dvh items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="bg-brand/10 text-brand-strong ring-brand/20 mb-3 inline-flex items-center justify-center rounded-2xl px-4 py-3 text-xl font-bold tracking-[0.18em] ring-1">
            ERP
          </span>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Admin Portal
          </h1>
        </div>

        <div className="panel p-6">
          {error && (
            <Alert tone="danger" className="mb-4 flex items-start gap-2">
              <i className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0" />
              <span>{error}</span>
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <TextInput
              label="Username"
              id="admin-username"
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              autoFocus
            />

            {/* Password field keeps its own wrapper so the reveal toggle can sit
                inside the input; TextInput handles the label and error slot. */}
            <div className="relative">
              <TextInput
                label="Password"
                id="admin-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="hover:bg-elevated absolute right-1.5 bottom-1.5 rounded-lg p-2 text-zinc-400 transition-colors hover:text-zinc-700"
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>

            <Button
              type="submit"
              id="admin-login-btn"
              variant="primary"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Spinner size="size-4" className="text-white" />
                  Signing in…
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-zinc-500">
          <i className="bi bi-shield-lock" />
          Admin accounts only. Contact your administrator for access.
        </p>
      </div>
    </div>
  );
}
